import { ChangeDetectionStrategy, Component, OnInit, ViewChild, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { App } from '@df/core/app';
import { DfDomModule } from '@df/dom/df-dom.module';
import { PwaIntegrationService } from '@df/module-pwa/pwa-integration.service';
import { IfNotStandaloneDirective } from '@df/module-pwa/utils/if-not-standalone.directive';
import { IfStandaloneDirective } from '@df/module-pwa/utils/if-standalone.directive';
import { AfterContentInitDirective, AfterContentInitDirectiveService } from '@df/ui/common/after-content-init.directive';
import { DfUiModule } from '@df/ui/df-ui.module';
import { IfUrlNotContainDirective } from '@df/ui/display/if-url-not-contain.directive';
import { ToggleModule } from '@df/ui/toggle/toggle.module';
import { UiComponent } from '@df/ui/ui.component';
import { EXIT_COMPONENT_EDIT_EVENT } from '@icms/core/integration/icms-core-integration-interfaces';
import { BehaviorSubject } from 'rxjs';
import { BasketAddNoticeComponent } from './basket/add-notice/basket-add-notice.component';
import { OffCanvasMenuComponent } from './layout/off-canvas/off-canvas-menu.component';
import { PageFooterComponent } from './layout/page/footer/page-footer.component';
import { PageHeaderComponent } from './layout/page/header/page-header.component';
import { PwaBackButtonComponent } from './module/pwa/back-button/pwa-back-button.component';
import { PwaBottomBarComponent } from './module/pwa/bottom-bar/pwa-bottom-bar.component';
import { ZendeskService } from './module/zendesk/zendesk.service';
import { IfNotTradeStoreDirective } from './multisite/trade/if-not-trade-store.directive';
import { IfTradeStoreDirective } from './multisite/trade/if-trade-store.directive';
import { TradeClassDirective } from './multisite/trade/trade-class.directive';
import { AppBusyStateComponent } from './ui/display/busy-state/app-busy-state.component';
import { RouterAppBusyStateComponent } from './ui/display/busy-state/router-app-busy-state.component';

@Component({
    selector: 'app-root',
    templateUrl: './app-root.html',
    styleUrls: ['./app-root.scss'],
    standalone: true,
    imports: [
        AfterContentInitDirective,
        AppBusyStateComponent,
        BasketAddNoticeComponent,
        DfDomModule,
        DfUiModule,
        IfNotTradeStoreDirective,
        IfNotStandaloneDirective,
        IfStandaloneDirective,
        IfTradeStoreDirective,
        IfUrlNotContainDirective,
        OffCanvasMenuComponent,
        PageFooterComponent,
        PageHeaderComponent,
        PwaBackButtonComponent,
        PwaBottomBarComponent,
        RouterAppBusyStateComponent,
        RouterOutlet,
        ToggleModule,
        TradeClassDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppRootComponent extends UiComponent implements OnInit {
    private _pageHeader?: PageHeaderComponent;
    /**
     * If off canvas is visible
     */
    readonly offCanvasState = new BehaviorSubject<boolean>(false);

    protected isAfterContentInit = false;

    private app = inject(App);
    private afterContentInitDirectiveService = inject(AfterContentInitDirectiveService);
    private pwaIntegrationService = inject(PwaIntegrationService);
    hasHeader = this.app.hasSSRCode;

    private zendeskService = inject(ZendeskService);

    override ngOnInit() {
        if (!this.app.hasSSRCode) {
            this.afterContentInitDirectiveService.lock(this);
        }
        this.zendeskService.load();
        this.offCanvasState.subscribe(state => {
            this.app.events.broadcast(EXIT_COMPONENT_EDIT_EVENT);
        });
        super.ngOnInit();
    }

    @ViewChild('pageHeader')
    set pageHeader(value: PageHeaderComponent) {
        if (!this.app.hasSSRCode) {
            if (value) {
                const subs = value.hasContent$.subscribe(hasContent => {
                    if (!this.hasHeader && hasContent) {
                        this.hasHeader = hasContent;
                        this.detectChanges();
                        this.timeoutService.setTimeout(() => {
                            subs.unsubscribe();
                            this.afterContentInitDirectiveService.unlock(this);
                        });
                    }
                });

                const sub2 = this.afterContentInitDirectiveService.ready.subscribe(ready => {
                    if (ready) {
                        this.isAfterContentInit = true;
                        this.detectChanges();
                    }
                    this.timeoutService.setTimeout(() => {
                        sub2.unsubscribe();
                    });
                });
            }
        }
        this._pageHeader = value;
    }
    get pageHeader(): PageHeaderComponent | undefined {
        return this._pageHeader;
    }
    get hasPageContentClass() {
        return this.isAfterContentInit || !this.app.isPlatformBrowser;
    }
    get isStandalone() {
        return !this.app.isPlatformBrowser || this.pwaIntegrationService.isStandalone;
    }
}
